import normalizeSearchText from '../utils/search-text';

const initDestinationAutocomplete = (module) => {
  const input = module.querySelector('[data-destination-search]');
  const resultsPanel = module.querySelector('[data-destination-results]');
  const config = window.vizaAzDestinationSearch;

  if (!input || !resultsPanel || !config?.ajaxUrl || !config?.nonce) {
    return;
  }

  const MAX_RESULTS = 8;
  // Süzgəc yerli olduğuna görə tək hərf də dərhal cavab verir.
  const MIN_QUERY_LENGTH = 1;

  let debounceTimer;
  let requestController;
  let activeIndex = -1;
  let results = [];
  // Kataloq səhifə ilə birlikdə gəlir, ona görə ilk açılışda şəbəkə gözlənilmir.
  let defaultResults = Array.isArray(config.catalogue) && config.catalogue.length ? config.catalogue : null;
  let pendingQuery = null;
  let shouldShowResults = false;

  // Azərbaycan əlifbasında İ/i və I/ı cütləri ingilis qaydası ilə səhv çevrilir,
  // ona görə dil kodu açıq göstərilir.
  const normalize = normalizeSearchText;

  /** Kataloqu yerli süzür: əvvəl adı sorğu ilə başlayanlar, sonra içində saxlayanlar. */
  const filterCatalogue = (query) => {
    const needle = normalize(query);
    const starts = [];
    const contains = [];

    (defaultResults || []).forEach((item) => {
      const title = normalize(item.title);

      if (title.startsWith(needle)) {
        starts.push(item);
      } else if (title.includes(needle)) {
        contains.push(item);
      }
    });

    return starts.concat(contains).slice(0, MAX_RESULTS);
  };

  const closeResults = () => {
    shouldShowResults = false;
    requestController?.abort();
    requestController = null;
    pendingQuery = null;
    resultsPanel.hidden = true;
    resultsPanel.replaceChildren();
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
    results = [];
  };

  const setActiveResult = (nextIndex) => {
    if (!results.length) {
      return;
    }

    activeIndex = (nextIndex + results.length) % results.length;

    results.forEach((result, index) => {
      const isActive = index === activeIndex;
      result.classList.toggle('is-active', isActive);
      result.setAttribute('aria-selected', String(isActive));
    });

    input.setAttribute('aria-activedescendant', results[activeIndex].id);
    results[activeIndex].scrollIntoView({ block: 'nearest' });
  };

  const showMessage = (message, isError = false) => {
    const status = document.createElement('p');
    status.className = `service-search__results-status${isError ? ' is-error' : ''}`;
    status.setAttribute('role', 'status');
    status.textContent = message;
    resultsPanel.replaceChildren(status);
    resultsPanel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    activeIndex = -1;
    results = [];
  };

  const showResults = (items) => {
    if (!items.length) {
      showMessage(config.noResultsText);
      return;
    }

    const fragment = document.createDocumentFragment();

    items.forEach((item, index) => {
      const link = document.createElement('a');
      link.id = `${resultsPanel.id}-option-${index}`;
      link.className = 'service-search__result';
      link.href = item.url;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');

      // Kataloq bayrağı mətn kimi verir; köhnə AJAX cavabı obyekt qaytarır.
      const flagUrl = 'string' === typeof item.flag ? item.flag : item.flag?.url;

      if (flagUrl) {
        const flag = document.createElement('img');
        flag.className = 'service-search__result-flag';
        flag.src = flagUrl;
        flag.alt = '';
        flag.width = 28;
        flag.height = 19;
        flag.loading = 'lazy';
        link.appendChild(flag);
      }

      const title = document.createElement('span');
      title.textContent = item.title;
      link.appendChild(title);
      fragment.appendChild(link);
    });

    resultsPanel.replaceChildren(fragment);
    resultsPanel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    activeIndex = -1;
    results = Array.from(resultsPanel.querySelectorAll('.service-search__result'));
  };

  const searchDestinations = async (query) => {
    if (pendingQuery === query) {
      return;
    }

    requestController?.abort();
    requestController = new AbortController();
    const currentController = requestController;
    pendingQuery = query;

    const body = new URLSearchParams({
      action: 'viza_az_search_destinations',
      nonce: config.nonce,
      query,
    });

    try {
      const response = await fetch(config.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: body.toString(),
        signal: currentController.signal,
      });
      const payload = await response.json();

      if (!response.ok || !payload.success || !Array.isArray(payload.data?.results)) {
        throw new Error('Invalid destination search response');
      }

      if (query === '') {
        defaultResults = payload.data.results;

        // Kataloq gec gəldisə, istifadəçinin artıq yazdığı mətnə görə süzülür.
        const typed = input.value.trim();

        if (shouldShowResults && typed.length >= MIN_QUERY_LENGTH) {
          showResults(filterCatalogue(typed));
          return;
        }
      }

      if (shouldShowResults && input.value.trim() === query) {
        showResults(payload.data.results);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && shouldShowResults && input.value.trim() === query) {
        showMessage(config.errorText, true);
      }
    } finally {
      if (requestController === currentController) {
        requestController = null;
        pendingQuery = null;
      }
    }
  };

  const showDefaultResults = () => {
    if (input.value.trim() !== '') {
      return;
    }

    shouldShowResults = true;

    if (defaultResults !== null) {
      showResults(defaultResults);
      return;
    }

    searchDestinations('');
  };

  input.addEventListener('focus', showDefaultResults);
  input.addEventListener('click', showDefaultResults);

  input.addEventListener('input', () => {
    const query = input.value.trim();
    window.clearTimeout(debounceTimer);

    shouldShowResults = true;

    if (query === '') {
      showDefaultResults();
      return;
    }

    if (query.length < MIN_QUERY_LENGTH) {
      requestController?.abort();
      closeResults();
      return;
    }

    // Kataloq yüklənibsə nəticə dərhal göstərilir — şəbəkə gözlənilmir.
    if (defaultResults !== null) {
      showResults(filterCatalogue(query));
      return;
    }

    // Kataloq hələ gəlməyibsə serverdən soruşulur; eyni zamanda kataloq da yüklənir
    // ki, növbəti hərflər artıq yerli süzülsün.
    searchDestinations('');
    debounceTimer = window.setTimeout(() => searchDestinations(query), 180);
  });

  input.addEventListener('keydown', (event) => {
    if (resultsPanel.hidden) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveResult(activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveResult(activeIndex - 1);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      window.location.assign(results[activeIndex].href);
    } else if (event.key === 'Escape') {
      closeResults();
    }
  });

  document.addEventListener('click', (event) => {
    if (!module.contains(event.target)) {
      closeResults();
    }
  });
};

export default function initServiceSearch() {
  const modules = document.querySelectorAll('[data-service-search]');

  modules.forEach((module) => {
    initDestinationAutocomplete(module);

    const tabGroup = module.querySelector('.service-search__tabs');

    if (!tabGroup) {
      return;
    }

    const tabs = Array.from(tabGroup.querySelectorAll('.service-search__tab'));
    let activeTab = tabGroup.querySelector('.service-search__tab.is-active') || tabs[0];

    if (!activeTab) {
      return;
    }

    let resizeFrame;

    const moveIndicator = (tab, shouldAnimate = true) => {
      if (!tab) {
        return;
      }

      tabGroup.classList.toggle('is-indicator-instant', !shouldAnimate);
      tabGroup.style.setProperty('--service-search-indicator-x', `${tab.offsetLeft}px`);
      tabGroup.style.setProperty('--service-search-indicator-width', `${tab.offsetWidth}px`);

      if (!shouldAnimate) {
        window.requestAnimationFrame(() => {
          tabGroup.classList.remove('is-indicator-instant');
        });
      }
    };

    moveIndicator(activeTab, false);
    tabGroup.classList.add('is-indicator-ready');

    // Şrift gec yükləndikdə tab enləri dəyişir; indikator yenidən ölçülməlidir.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => moveIndicator(activeTab, false));
    }

    tabs.forEach((tab) => {
      tab.addEventListener('mouseenter', () => moveIndicator(tab));
      tab.addEventListener('focus', () => moveIndicator(tab, false));
    });

    tabGroup.addEventListener('mouseleave', () => moveIndicator(activeTab));
    tabGroup.addEventListener('focusout', (event) => {
      if (!tabGroup.contains(event.relatedTarget)) {
        moveIndicator(activeTab, false);
      }
    });

    window.addEventListener('resize', () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => moveIndicator(activeTab, false));
    });

    const panels = Array.from(module.querySelectorAll('[data-service-search-panel]'));
    const panelTabs = tabs.filter((tab) => tab.dataset.servicePanel);

    if (!panels.length || !panelTabs.length) {
      return;
    }

    /*
     * Travelpayouts widget-i özünü açıq shadow DOM daxilində render edir,
     * ona görə səhifənin CSS-i oraya çatmır. Radius tokenini shadow root-un
     * içinə inject edirik ki, düymələr saytın "Axtar" düyməsi ilə eyni olsun.
     */
    const widgetShadowStyles = [
      ':host, .cascoon { --radius-size: 16px !important; --border-radius: 16px !important; }',
      '.form-submit, input[type="submit"] { border-radius: 16px !important; }',
    ].join('\n');

    const styleShadowHost = (host) => {
      const root = host.shadowRoot;

      if (!root || root.querySelector('[data-viza-az-widget-style]')) {
        return;
      }

      const style = document.createElement('style');

      style.setAttribute('data-viza-az-widget-style', '');
      style.textContent = widgetShadowStyles;
      root.appendChild(style);
    };

    const styleWidgetShadowRoots = (panel) => {
      const apply = () => {
        panel.querySelectorAll('*').forEach((element) => {
          if (element.shadowRoot) {
            styleShadowHost(element);
          }
        });
      };

      apply();

      const observer = new MutationObserver(apply);

      observer.observe(panel, { childList: true, subtree: true });
      // Widget render olunduqdan sonra izləməyə ehtiyac qalmır.
      window.setTimeout(() => observer.disconnect(), 20000);
    };

    // Widget skripti ağırdır; yalnız tab ilk dəfə açıldıqda yüklənir.
    const loadWidget = (panel, src) => {
      if (!src || panel.dataset.widgetLoaded) {
        return;
      }

      const host = panel.querySelector('[data-service-widget-host]');

      if (!host) {
        return;
      }

      panel.dataset.widgetLoaded = 'true';
      styleWidgetShadowRoots(panel);

      /*
       * Widget layoutunu öz konteynerinin enindən seçir. Skript işə düşməzdən
       * əvvəl brauzeri yeni eni hesablamağa məcbur edirik, əks halda widget
       * köhnə dar eni görüb sahələri iki sətrə salır.
       */
      void panel.offsetWidth;

      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      // Travelpayouts skripti özünü document.currentScript-in yanında render edir,
      // ona görə script elementi birbaşa host div-in içinə əlavə olunur.
      script.addEventListener('load', () => {
        const status = host.querySelector('.service-search__widget-status');

        if (status) {
          status.remove();
        }
      });
      script.addEventListener('error', () => {
        const status = host.querySelector('.service-search__widget-status');

        if (status) {
          status.textContent = status.dataset.errorText || status.textContent;
        }

        panel.dataset.widgetLoaded = '';
      });
      host.appendChild(script);
    };

    const showPanel = (tab) => {
      const targetId = tab.dataset.servicePanel;
      const target = document.getElementById(targetId);

      if (!target) {
        return;
      }

      panels.forEach((panel) => {
        panel.hidden = panel !== target;
      });

      tabs.forEach((item) => {
        const isCurrent = item === tab;

        item.classList.toggle('is-active', isCurrent);

        if (isCurrent) {
          item.setAttribute('aria-current', 'page');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      activeTab = tab;
      module.classList.toggle('service-search--widget-open', Boolean(tab.dataset.serviceWidgetSrc));
      loadWidget(target, tab.dataset.serviceWidgetSrc);
      moveIndicator(tab);
    };

    panelTabs.forEach((tab) => {
      tab.addEventListener('click', () => showPanel(tab));
    });

    // Səhifə widget tabı aktiv vəziyyətdə açılıbsa, skript dərhal lazımdır.
    const openPanel = panels.find((panel) => !panel.hidden);
    const openTab = openPanel ? panelTabs.find((tab) => tab.dataset.servicePanel === openPanel.id) : null;

    if (openPanel && openTab) {
      module.classList.toggle('service-search--widget-open', Boolean(openTab.dataset.serviceWidgetSrc));
      loadWidget(openPanel, openTab.dataset.serviceWidgetSrc);
    }
  });
}
